import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import Card from '@/components/ui/cards/Card';
import CardHeader from '@/components/ui/cards/CardHeader';
import Breadcrumb from '@/components/common/Breadcrumb';
import { InputSelect } from '@/components/ui/inputs/input-select';
import CardBody from '@/components/ui/cards/CardBody';
import { Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import CurrencyCodeAdd from '@/components/common/CurrencyCodeAdd';
import { getByIdOrderService, updateOrderService } from '@/services/OrderServices';
import validationErrors from '@/lib/validationErrors';
import { useParams } from 'react-router-dom';
import { getFileUrl } from '@/services/CommonService';
import POSReceiptModal from './POSReceiptModal';
import Moment from 'moment';

const PosOrderDetailsPage = ({ pageTitle }) => {
    const { id } = useParams();
    const { posOrder, generalSetting } = useSelector((state) => state?.GeneralSetting);
    const dispatch = useDispatch();
    const {
        register,
        setValue,
        getValues,
        formState: { errors },
        handleSubmit,
        control,
        reset,
    } = useForm();
    const [isLoading, setIsLoading] = useState(false);
    const [orderData, setOrderData] = useState();
    const [paymentStatus, setPaymentStatus] = useState(true);
    const [isFormSumbmit, setIsFormSumbmit] = useState(false);
    const [receiptModalOpen, setReceiptModalOpen] = useState(false);
    const orderStatusOptionList = [
        { value: 1, label: 'Pending' },
        { value: 2, label: 'Placed' },
        { value: 3, label: 'Accept' },
        { value: 4, label: 'Processing' },
        { value: 5, label: 'Out for delivery' },
        { value: 6, label: 'Delivered' },
        { value: 7, label: 'Canceled' },
        { value: 8, label: 'Rejected' },
        { value: 9, label: 'Returned' },
    ];

    const paymentStatusOptionList = [
        { value: 1, label: 'Unpaid' },
        { value: 2, label: 'Paid' },
        { value: 3, label: 'Failed' },
        { value: 4, label: 'Waiting For Approval' },
    ];

    useEffect(() => {
        if (id && id != '') {
            getByIdOrderData(id);
        }
    }, [id]);

    const getItemVariations = (itemVariations) => {
        try {
            // Parse the itemVariations if it is a string
            const parsedItemVariations = typeof itemVariations === 'string' ? JSON.parse(itemVariations) : itemVariations;

            // Handle null or invalid data gracefully
            if (!parsedItemVariations || !parsedItemVariations._ids) {
                return null;
            }

            // Map over variations and generate JSX elements
            return parsedItemVariations._ids.map((element, index) => {
                const variationName = parsedItemVariations.name[index]; // Assuming 'name' holds variation name
                const variationAttributeName = parsedItemVariations.attribute_name[index]; // Assuming 'name' holds variation name
                return (
                    <span key={index}>
                        {variationAttributeName}: {variationName}
                        {index < parsedItemVariations._ids.length - 1 && ', '}
                    </span>
                );
            });
        } catch (error) {
            console.error('Error parsing item variations:', error);
            return null;
        }
    };

    const getItemExtra = (itemExtars) => {
        try {
            const parsedItemExtars = typeof itemExtars === 'string' ? JSON.parse(itemExtars) : itemExtars;

            if (!parsedItemExtars || !parsedItemExtars._ids) {
                return null;
            }

            return parsedItemExtars._ids.map((element, index) => {
                const extraName = parsedItemExtars.name[index];
                return (
                    <span key={index}>
                        {extraName}
                        {index < parsedItemExtars._ids.length - 1 && ', '}
                    </span>
                );
            });
        } catch (error) {
            console.error('Error parsing item variations:', error);
            return null;
        }
    };

    const getByIdOrderData = (OrderId) => {
        setIsLoading(true);
        dispatch(getByIdOrderService(OrderId))
            .then((res) => {
                setOrderData(res?.data?.data);
                setValue('status', res?.data?.data?.status);
                setValue('payment_status', res?.data?.data?.payment_status);
                setIsLoading(false);
            })
            .catch(({ message, errorData, statusCode }) => {
                validationErrors(errorData, message);
                setIsLoading(false);
            });
    };

 const handleChangeStatus = (name, option) => {
     const numericValue = option?.value ?? option; // fall-back if a number is passed

     if (name === 'status') {
         setValue('status', numericValue);
     } else if (name === 'payment_status') {
         setValue('payment_status', numericValue);
     }

     handleSubmit(onSubmit)();   // submit immediately (keeps old behaviour)
 };

    const onSubmit = (data) => {
        setIsFormSumbmit(true);
        dispatch(updateOrderService(id, data))
            .then((res) => {
                setIsFormSumbmit(false);
                getByIdOrderData(id);
            })
            .catch(({ message, errorData, statusCode }) => {
                validationErrors(errorData, message);
                setIsFormSumbmit(false);
            });
    };

    return (
        <div className="order-details-wrapper">
            <Breadcrumb pageTitle={pageTitle} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Card className="p-4 sm:col-span-2">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="flex flex-wrap gap-y-5 items-end justify-between">
                            <div>
                                <div className="flex flex-wrap items-start gap-y-2 gap-x-6 mb-5">
                                    <p className="text-2xl font-medium">
                                        Order Id:<span className="text-heading">#{orderData?.order_serial_no}</span>
                                    </p>
                                    <div className="flex items-center gap-2 mt-1.5">
                                        <span
                                            className={cn(
                                                'text-xs capitalize h-5 leading-5 px-2 rounded-3xl text-[#FB4E4E] bg-[#FFDADA]',
                                                orderData?.payment_status === 2 ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100'
                                            )}>
                                            {orderData?.payment_status && paymentStatusOptionList.find((da) => da.value === orderData?.payment_status).label}
                                        </span>
                                        <span className={cn('text-xs capitalize px-2 rounded-3xl', orderData?.status === 2 ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100')}>
                                            {orderData?.status && orderStatusOptionList.find((da) => da.value === orderData?.status).label}
                                        </span>
                                    </div>
                                </div>
                                <ul className="flex flex-col gap-2">
                                    <li className="flex items-center gap-2">
                                        <i className="lab lab-calendar-line lab-font-size-16"></i>
                                        <span className="text-xs">{Moment(orderData?.order_datetime).format('Y-M-D hh:mm:ss')}</span>
                                    </li>
                                    <li className="text-xs">
                                        Payment Type:<span className="text-heading">{orderData?.payment_method_id && orderData?.payment_gateway ? orderData?.payment_gateway?.name : 'Cash'}</span>
                                    </li>
                                    <li className="text-xs">
                                        Order Type:<span className="text-heading">POS</span>
                                    </li>
                                    <li className="text-xs">
                                        Delivery Time:<span className="text-heading">{orderData?.delivery_time && orderData?.delivery_time != '' ? orderData?.delivery_time : ''}</span>
                                    </li>
                                    <li className="text-xs">
                                        Token No:<span className="text-heading">#{orderData?.token}</span>
                                    </li>
                                </ul>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                <Controller
                                    control={control}
                                    rules={{ required: true }}
                                    name="payment_status"
                                    render={({ field }) => (
                                        <InputSelect
                                            {...field}
                                            className="capitalize text-primary border-primary max-w-[160px] min-w-[160px]"
                                            id={'payment_status'}
                                            labelText={'Payment Status'}
                                            value={paymentStatusOptionList.find((data) => data.value == getValues('payment_status')) || null}
                                            onChange={(e) => handleChangeStatus('payment_status', e)}
                                            options={paymentStatusOptionList}
                                            errorType={errors?.payment_status?.type}
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    rules={{ required: true }}
                                    name="status"
                                    render={({ field }) => (
                                        <InputSelect
                                            {...field}
                                            className="capitalize text-primary border-primary max-w-[160px] min-w-[160px]"
                                            id={'status'}
                                            labelText={'Order Status'}
                                            value={orderStatusOptionList.find((data) => data.value == getValues('status')) || null}
                                            onChange={(e) => handleChangeStatus('status', e)}
                                            options={orderStatusOptionList}
                                            errorType={errors?.status?.type}
                                        />
                                    )}
                                />
                                <button type="button" onClick={() => setReceiptModalOpen(true)} className="flex items-center justify-center gap-2 px-4 h-[38px] rounded shadow-db-card bg-primary">
                                    <i className="lab lab-printer-line lab-font-size-16 text-white"></i>
                                    <span className="text-sm capitalize text-white">Print Receipt</span>
                                </button>
                            </div>
                        </div>
                    </form>
                </Card>
                <div className="left-part-card">
                    <Card>
                        <CardHeader isBorder={true}>
                            <h3 className="db-card-title">{pageTitle}</h3>
                        </CardHeader>
                        <CardBody>
                            {orderData?.order_items &&
                                orderData?.order_items.length > 0 &&
                                orderData?.order_items?.map((cart, index) => (
                                    <div className="mb-3 pb-3 border-b last:mb-0 last:pb-0 last:border-b-0 border-gray-2">
                                        <div className="flex items-center gap-3 relative" key={index}>
                                            <h3 className="absolute top-5 -left-3 text-sm w-[26px] h-[26px] leading-[26px] text-center rounded-full text-white bg-heading">{cart?.quantity}</h3>
                                            <img className="w-16 h-16 rounded-lg flex-shrink-0" src={getFileUrl(orderData?.items[index].file_name, 'product')} alt="thumbnail" />
                                            <div className="w-full">
                                                <Link to="#" className="text-sm font-medium capitalize transition text-heading hover:underline">
                                                    {orderData?.items[index].name}
                                                </Link>
                                                {cart.item_variations && cart.item_variations != '' && <p className="capitalize text-xs mb-1.5">{getItemVariations(cart.item_variations)}</p>}
                                                <h3 className="text-xs font-semibold">
                                                    <CurrencyCodeAdd price={cart.total_price} />
                                                </h3>
                                            </div>
                                        </div>
                                        <ul className="flex flex-col gap-1.5 mt-2">
                                            {cart.item_extras && cart.item_extras != '' && <li className="flex gap-1">{getItemExtra(cart.item_extras)}</li>}
                                            {cart.instruction && cart.instruction != '' && (
                                                <li className="flex gap-1">
                                                    <h3 className="capitalize text-xs w-fit whitespace-nowrap">Instruction:</h3>
                                                    <p className="text-xs">{cart.instruction}</p>
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                ))}
                        </CardBody>
                    </Card>
                </div>
                <div className="right-part-card">
                    <Card className="p-1 mb-6">
                        <ul className="flex flex-col gap-2 p-3 border-b border-dashed border-[#EFF0F6]">
                            <li className="flex items-center justify-between text-heading">
                                <span className="text-sm leading-6 capitalize">Subtotal</span>
                                <span className="text-sm leading-6 capitalize">
                                    <CurrencyCodeAdd price={orderData?.subtotal} />
                                </span>
                            </li>
                            {orderData?.total_tax && orderData?.total_tax > 0 ? (
                                <li className="flex items-center justify-between text-heading">
                                    <span className="text-sm leading-6 capitalize">Total Tax</span>
                                    <span className="text-sm leading-6 capitalize">
                                        <CurrencyCodeAdd price={orderData?.total_tax} />
                                    </span>
                                </li>
                            ) : (
                                <></>
                            )}
                            {orderData?.discount && orderData?.discount > 0 ? (
                                <li className="flex items-center justify-between text-heading">
                                    <span className="text-sm leading-6 capitalize">Discount</span>
                                    <span className="text-sm leading-6 capitalize">
                                        <CurrencyCodeAdd price={orderData?.discount} />
                                    </span>
                                </li>
                            ) : (
                                <></>
                            )}
                        </ul>
                        <div className="flex items-center justify-between p-3">
                            <h4 className="text-sm leading-6 font-bold capitalize">Total</h4>
                            <h5 className="text-sm leading-6 font-bold capitalize">
                                <CurrencyCodeAdd price={orderData?.total} />
                            </h5>
                        </div>
                    </Card>
                    <Card>
                        <CardHeader isBorder={true}>
                            <h3 className="db-card-title">Delivery Information</h3>
                        </CardHeader>
                        <CardBody>
                            <div className="flex items-center gap-3 mb-4">
                                <img className="w-8 rounded-full" src={'/assets/images/default/profile-image.jpg'} alt="avatar" />
                                <h4 className="font-semibold text-sm capitalize text-[#374151]">{orderData?.customers?.name}</h4>
                            </div>
                            <ul className="flex flex-col gap-3 py-4 mb-4 border-y border-[#EFF0F6]">
                                <li className="flex items-center gap-2.5">
                                    <i className="lab lab-mail lab-font-size-14"></i>
                                    <span className="text-xs">{orderData?.customers?.email}</span>
                                </li>
                                <li className="flex items-center gap-2.5">
                                    <i className="lab lab-call-calling-linear lab-font-size-14"></i>
                                    <span className="text-xs">+{orderData?.customers?.phone_no}</span>
                                </li>
                            </ul>
                        </CardBody>
                    </Card>
                </div>
            </div>
            <POSReceiptModal modalOpen={receiptModalOpen} setModalOpen={setReceiptModalOpen} receiptOrderId={orderData?._id} />
        </div>
    );
};

export default PosOrderDetailsPage;
